import type { StreamConnectionError } from '@/infra'
import { StreamType } from '@/infra'

export type StreamConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

export interface StreamConnectionState {
  readonly status: StreamConnectionStatus
  readonly connectionId: string | null
  readonly error: StreamConnectionError | null
}

export interface EventStreamConnectionsState {
  readonly streams: ReadonlyMap<StreamType, StreamConnectionState>
  readonly reconnectAttempts: number
  readonly isFullyConnected: boolean
}

export function createInitialStreamConnectionState(): StreamConnectionState {
  return {
    status: 'disconnected',
    connectionId: null,
    error: null,
  }
}

const DEFAULT_STREAM_TYPES: readonly StreamType[] = [
  StreamType.CONVERSATION,
  StreamType.ACTIVITY,
  StreamType.STATUS,
]

export function createInitialEventStreamConnectionsState(
  streamTypes: readonly StreamType[] = DEFAULT_STREAM_TYPES,
): EventStreamConnectionsState {
  const streams = new Map<StreamType, StreamConnectionState>()
  for (const type of streamTypes) {
    streams.set(type, createInitialStreamConnectionState())
  }
  return {
    streams,
    reconnectAttempts: 0,
    isFullyConnected: false,
  }
}

export function deriveIsFullyConnected(
  streams: ReadonlyMap<StreamType, StreamConnectionState>,
): boolean {
  if (streams.size === 0) return false
  for (const state of streams.values()) {
    if (state.status !== 'connected') return false
  }
  return true
}

export function updateStreamState(
  currentState: EventStreamConnectionsState,
  streamType: StreamType,
  newStreamState: StreamConnectionState,
): EventStreamConnectionsState {
  const newStreams = new Map(currentState.streams)
  newStreams.set(streamType, newStreamState)

  return {
    ...currentState,
    streams: newStreams,
    isFullyConnected: deriveIsFullyConnected(newStreams),
  }
}
